import moment from 'moment';
import QRCode from 'qrcode';
import { checkServiceIds, isCore } from './qualityChecks';

// ─── Quality Control Worksheet print ─────────────────────────────────────────
// Builds a self-contained, printable worksheet for one service order and opens
// it in a new window with the print dialog. It mirrors the digital Quality tab
// field-for-field so a mechanic can fill it by hand on the shop floor, then snap
// a photo that syncs back to the digital record. The header carries a QR code of
// the SO stock number so the uploaded photo maps unambiguously to this order.
//
// Modeled on MechanicQueuePrint.js (self-contained HTML → window.open → print).

const escapeHtml = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const methodLabels = {
  measured: 'Measured',
  road_test: 'Road test',
  obd_scan: 'OBD scan',
  visual: 'Visual',
  functional: 'Functional',
};

// The spec column: a measured target/range, the multistate options, or a dash.
const specCell = (c) => {
  if (c.type === 'measured') {
    const parts = [];
    if (typeof c.expected === 'number') parts.push(`target ${c.expected}`);
    if (typeof c.min === 'number' || typeof c.max === 'number') {
      parts.push(`${typeof c.min === 'number' ? c.min : '–'}…${typeof c.max === 'number' ? c.max : '–'}`);
    }
    const body = parts.join(' &nbsp; ');
    return c.unit ? `${body} <b>${escapeHtml(c.unit)}</b>` : (body || '—');
  }
  if (c.type === 'multistate') return 'Pass&nbsp; / &nbsp;Fail&nbsp; / &nbsp;N/A';
  return '—';
};

// The write-in cell: show a recorded reading if one exists, else a blank line.
const resultCell = (c) => {
  if (c.type === 'measured') {
    if (c.actual !== null && c.actual !== undefined) {
      return `${escapeHtml(c.actual)}${c.unit ? ' ' + escapeHtml(c.unit) : ''}`;
    }
    return '<span class="blank"></span>';
  }
  if (c.type === 'multistate') {
    if (c.result) return escapeHtml(c.result === 'na' ? 'N/A' : c.result.charAt(0).toUpperCase() + c.result.slice(1));
    return '&#9711; Pass &nbsp; &#9711; Fail &nbsp; &#9711; N/A';
  }
  return `<span class="blank"></span>`; // text observation
};

const serviceBlock = (svc, checks) => {
  // Core first, then extended, so the required rows read top-down and the
  // numbering matches the Quality tab, where extended sits below the core list.
  const ordered = [...checks.filter(isCore), ...checks.filter(c => !isCore(c))];
  const rows = ordered.map((c, i) => `
    <tr class="${isCore(c) ? '' : 'ext'}">
      <td class="idx">${i + 1}${isCore(c) ? '' : '<div class="extmark">EXT</div>'}</td>
      <td class="check">
        <div class="check-label">${escapeHtml(c.label)}</div>
        ${c.method ? `<div class="method">${escapeHtml(methodLabels[c.method] || c.method)}</div>` : ''}
      </td>
      <td class="spec">${specCell(c)}</td>
      <td class="result">${resultCell(c)}</td>
      <td class="init"></td>
    </tr>`).join('');

  return `
    <div class="svc-block">
      <div class="svc-head">
        <div class="svc-name">${escapeHtml(svc.name)}</div>
        ${svc.tag ? `<div class="svc-tag">${escapeHtml(svc.tag)}</div>` : ''}
      </div>
      <table>
        <thead>
          <tr>
            <th class="idx">#</th>
            <th>Check</th>
            <th class="spec">Spec (verify vs. manual)</th>
            <th class="result">Actual / Result</th>
            <th class="init">Init</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
};

const STYLES = `
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Helvetica, Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 24px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #023e84; padding-bottom: 12px; margin-bottom: 16px; }
  h1 { margin: 0; font-size: 24px; color: #023e84; }
  .subtitle { color: #555; font-size: 13px; margin-top: 3px; }
  .so { font-family: monospace; font-weight: 700; font-size: 15px; }
  .head-right { display: flex; align-items: flex-start; gap: 16px; }
  .carphoto img { width: 150px; height: 108px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd; display: block; }
  .qr { text-align: center; }
  .qr img { width: 108px; height: 108px; }
  .qr .cap { font-size: 9px; color: #777; max-width: 120px; margin-top: 2px; }
  .svc-block { margin-bottom: 18px; }
  .svc-head { display: flex; align-items: baseline; gap: 8px; margin-bottom: 4px; }
  .svc-name { font-weight: 700; font-size: 15px; }
  .svc-tag { font-size: 11px; color: #666; border: 1px solid #ccc; border-radius: 10px; padding: 1px 8px; }
  table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  th, td { text-align: left; padding: 7px 8px; border: 1px solid #d0d0d0; vertical-align: top; }
  thead th { background: #023e84; color: #fff; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; }
  td.idx, th.idx { width: 26px; text-align: center; color: #777; }
  td.spec, th.spec { width: 30%; }
  td.result, th.result { width: 26%; }
  td.init, th.init { width: 52px; }
  .check-label { font-weight: 600; }
  .method { font-size: 10px; color: #777; text-transform: uppercase; letter-spacing: 0.3px; margin-top: 2px; }
  /* Extended checks are optional depth — present on paper, visually secondary. */
  tr.ext td { background: #fafafa; }
  tr.ext td.idx { border-left: 3px solid #b0b0b0; }
  tr.ext .check-label { font-weight: 500; }
  .extmark { font-size: 8px; color: #999; letter-spacing: 0.4px; margin-top: 2px; }
  .blank { display: block; border-bottom: 1px solid #999; min-height: 16px; }
  .foot { margin-top: 18px; padding-top: 8px; border-top: 1px solid #e0e0e0; font-size: 10px; color: #999; }
  .signoff { margin-top: 14px; font-size: 12.5px; }
  .signoff .line { display: inline-block; border-bottom: 1px solid #333; min-width: 200px; margin: 0 12px 0 6px; }
  @media print {
    body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    thead th { background: #023e84 !important; }
    /* Let a long service block flow across pages instead of forcing the whole
       (possibly taller-than-a-page) block onto a fresh sheet — the latter left a
       blank/short trailing page. Keep the column header repeated on each page and
       never split an individual check row or orphan a service name at page bottom. */
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; break-inside: avoid; }
    .svc-head { page-break-after: avoid; break-after: avoid; }
    .svc-block:last-of-type { margin-bottom: 0; }
  }
`;

// printQualityWorksheet({ order, stockNumber }) — async (QR generation).
// Returns false if the popup was blocked, true otherwise.
export async function printQualityWorksheet({ order, stockNumber }) {
  const car = (order && order.car) || {};
  const carTitle = car.title || [car.year, car.make, car.model].filter(Boolean).join(' ') || '';
  const checks = (order && order.qualityChecks) || [];
  const services = ((order && order.services) || []).filter(s => !s.declined);

  // Vehicle-level checks print once at the top; service-scope checks print under
  // every service they cover. Grouping matches the Quality tab — a check that
  // covers several lines via serviceIds used to key off its bare serviceId and
  // so went missing from the other lines' blocks.
  const vehicleChecks = checks.filter(c => c.scope === 'vehicle');
  const byService = {};
  for (const c of checks) {
    if (c.scope === 'vehicle') continue;
    for (const sid of checkServiceIds(c)) (byService[sid] = byService[sid] || []).push(c);
  }

  const vehicleBlock = vehicleChecks.length
    ? serviceBlock({ name: 'Vehicle checks — perform once' }, vehicleChecks)
    : '';

  // Only services that actually have their own checks make it onto the worksheet.
  const serviceBlocks = services
    .filter(s => byService[s.id] && byService[s.id].length)
    .map(s => serviceBlock({ name: s.name || s.description || 'Service', tag: s.tag }, byService[s.id]))
    .join('');

  const blocks = vehicleBlock + serviceBlocks;
  if (!blocks) return true; // nothing to print; caller treats as no-op success

  // Scanning the QR opens this order in the tools app, focused on the Quality
  // tab, so a mechanic can record results / upload this sheet from their phone.
  // Hardcode the prod origin — a phone can't reach a localhost dev server.
  const qrUrl = `https://tools.skywayclassics.com/?so=${encodeURIComponent(stockNumber)}&soTabs=Quality`;
  let qrImg = '';
  try {
    const dataUrl = await QRCode.toDataURL(qrUrl, { margin: 1, width: 216 });
    qrImg = `<div class="qr"><img src="${dataUrl}" alt="${escapeHtml(stockNumber)}"><div class="cap">Scan to open ${escapeHtml(stockNumber)} → Quality tab</div></div>`;
  } catch {
    qrImg = `<div class="qr"><div class="so">${escapeHtml(stockNumber)}</div></div>`;
  }

  // Car photo for the worksheet header — prefer the cover thumbnail, then fall
  // back to the first available photo from any image field (some cars have shots
  // uploaded but no cover set). Skipped silently if the car has no images at all.
  const firstFrom = (v) => (Array.isArray(v) ? v.find(Boolean) : v) || '';
  const carPhoto =
    car.thumbnail ||
    firstFrom(car.ext_images) ||
    firstFrom(car.interior_images) ||
    firstFrom(car.engine_images) ||
    firstFrom(car.under_images) ||
    firstFrom(car.stamping_images) ||
    car.vin_image ||
    '';
  const carImg = carPhoto
    ? `<div class="carphoto"><img src="${escapeHtml(carPhoto)}" alt="${escapeHtml(carTitle)}"></div>`
    : '';

  const printedAt = moment().format('MMM D, YYYY h:mm A');
  const html = `<!doctype html><html><head><meta charset="utf-8">
    <title>Quality Worksheet — ${escapeHtml(stockNumber)}</title>
    <style>${STYLES}</style></head><body>
    <div class="head">
      <div>
        <h1>Quality Control Worksheet</h1>
        <div class="subtitle"><span class="so">${escapeHtml(stockNumber)}</span>${carTitle ? ' · ' + escapeHtml(carTitle) : ''}</div>
        <div class="subtitle">Mechanic: ________________________ &nbsp;&nbsp; Date: ______________</div>
      </div>
      <div class="head-right">
        ${carImg}
        ${qrImg}
      </div>
    </div>
    ${blocks}
    <div class="signoff">
      QC passed by: <span class="line"></span> Date: <span class="line" style="min-width:120px;"></span>
    </div>
    <div class="foot">
      Specs shown are AI-suggested defaults — verify each against the service manual and correct as needed.
      Record the actual reading or circle the result, initial each check, then photograph this sheet into the Quality tab. Printed ${escapeHtml(printedAt)}.
    </div>
  </body></html>`;

  const win = window.open('', '_blank');
  if (!win) return false; // popup blocked
  win.document.open();
  win.document.write(html);
  win.document.close();
  // Print once the document's images have downloaded. The car photo is a remote
  // CDN image (the QR is an inline data URL), so firing print() too early left it
  // blank on the printout. Wait for every <img> to settle, then print; a hard cap
  // guarantees we never hang if an image stalls.
  let printed = false;
  const printOnce = () => { if (printed) return; printed = true; try { win.focus(); win.print(); } catch (e) { /* noop */ } };
  const printWhenImagesReady = () => {
    let imgs;
    try { imgs = Array.from(win.document.images || []); } catch (e) { printOnce(); return; }
    const pending = imgs.filter((img) => !img.complete);
    if (pending.length === 0) { printOnce(); return; }
    let remaining = pending.length;
    const settle = () => { remaining -= 1; if (remaining <= 0) printOnce(); };
    pending.forEach((img) => { img.addEventListener('load', settle); img.addEventListener('error', settle); });
  };
  win.onload = printWhenImagesReady;
  setTimeout(printWhenImagesReady, 300);
  setTimeout(printOnce, 5000); // hard cap in case an image never settles
  return true;
}

export default printQualityWorksheet;
